const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 9;
const PACKAGE_LIST_API = 'https://package.yonyoucloud.com/api/package/list';
const PACKAGE_HISTORY_API = 'https://package.yonyoucloud.com/api/package/packageHistory';
const DICT_LIST_API = 'https://package.yonyoucloud.com/api/yoncode/dict/list';
const MARKET_FETCH_PAGE_SIZE = 500;
const PACKAGE_HISTORY_PAGE_SIZE = 20;
const SKILL_FILTER_DICT_TYPE = 'skill_filter';
const PLUGIN_FILTER_DICT_TYPE = 'plugin_filter';
export const SKILL_FALLBACK_CATEGORY_KEY = 'other';
export const SKILL_FALLBACK_CATEGORY_LABEL = '其他';
export const PLUGIN_FALLBACK_TYPE_KEY = 'general';
export const PLUGIN_FALLBACK_TYPE_LABEL = '通用';
const skillTenantFilterOptions = [
    { key: 'qyic8c7o', label: '租户技能' },
    { key: '0', label: '官方技能' },
];
const pluginTenantFilterOptions = [
    { key: 'qyic8c7o', label: '租户插件' },
    { key: '0', label: '官方插件' },
];
let skillItemsPromise = null;
let pluginItemsPromise = null;
let skillFiltersPromise = null;
let pluginFiltersPromise = null;
const packageHistoryPromises = new Map();
const emptySkillFilters = {
    products: [],
    categories: [],
    sources: [],
    tenants: skillTenantFilterOptions,
};
const emptyPluginFilters = {
    types: [],
    sources: [],
    tenants: pluginTenantFilterOptions,
};
function normalize(value) {
    return (value || '').trim().toLowerCase();
}
function includesKeyword(fields, keyword) {
    if (!keyword)
        return true;
    return fields.some((field) => normalize(field).includes(keyword));
}
function paginate(items, page = DEFAULT_PAGE, pageSize = DEFAULT_PAGE_SIZE) {
    const total = items.length;
    const safePageSize = Math.max(1, pageSize);
    const pageCount = Math.max(1, Math.ceil(total / safePageSize));
    const safePage = Math.min(Math.max(1, page), pageCount);
    const start = (safePage - 1) * safePageSize;
    return {
        items: items.slice(start, start + safePageSize),
        total,
        page: safePage,
        pageSize: safePageSize,
    };
}
function toText(value, fallback = '') {
    if (typeof value === 'string')
        return value;
    if (typeof value === 'number')
        return String(value);
    return fallback;
}
function hasMeaningfulDescriptionText(value) {
    const text = value.trim();
    if (!text || text.toLowerCase() === 'null' || text.toLowerCase() === 'undefined')
        return false;
    const visibleText = text
        .replace(/<[^>]*>/g, '')
        .replace(/&(?:[a-z]+|#\d+|#x[\da-f]+);/gi, '')
        .trim();
    return /[\p{L}\p{N}]/u.test(visibleText);
}
function resolveSkillDescription(skillDescription, info) {
    const primaryDescription = toText(skillDescription).trim();
    if (hasMeaningfulDescriptionText(primaryDescription))
        return primaryDescription;
    return toText(info).trim();
}
function normalizeImageUrl(value) {
    const text = toText(value).trim();
    if (!text)
        return undefined;
    if (/^(https?:)?\/\//i.test(text) || text.startsWith('/') || /^data:image\//i.test(text))
        return text;
    return undefined;
}
function resolvePackageIconUrl(item) {
    return (normalizeImageUrl(item.skillIcon) ||
        normalizeImageUrl(item.icon) ||
        normalizeImageUrl(item.image));
}
function toNumber(value, fallback = 0) {
    const next = Number(value);
    return Number.isFinite(next) ? next : fallback;
}
function toRating(value) {
    if (value === null || value === undefined)
        return undefined;
    if (typeof value === 'string' && !value.trim())
        return undefined;
    const next = Number(value);
    return Number.isFinite(next) ? Math.min(5, Math.max(0, next)) : undefined;
}
function safeJsonParse(value) {
    if (typeof value !== 'string' || !value.trim())
        return null;
    try {
        return JSON.parse(value);
    }
    catch {
        return null;
    }
}
function unique(values) {
    return Array.from(new Set(values));
}
function pickColor(seed) {
    const colors = ['red', 'blue', 'purple', 'orange', 'green'];
    const index = Math.abs(seed.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0)) % colors.length;
    return colors[index];
}
function formatAuthor(author, fallback = '') {
    if (typeof author === 'string')
        return author;
    if (author && typeof author === 'object' && 'name' in author) {
        return toText(author.name, fallback);
    }
    return fallback;
}
function buildVersion(version, readmeFile, updatedAt, changeLog) {
    if (!version)
        return [];
    return [
        {
            version,
            sourceCodeUrl: readmeFile || undefined,
            updatedAt,
            changeLog: changeLog || undefined,
        },
    ];
}
function toTimestamp(value) {
    const timestamp = Date.parse(value);
    return Number.isFinite(timestamp) ? timestamp : 0;
}
function normalizeDictOption(option) {
    if (!option || typeof option !== 'object')
        return null;
    const record = option;
    const key = toText(record.key);
    const label = toText(record.label);
    if (!key || !label)
        return null;
    return { key, label };
}
function parseDictOptions(value) {
    const parsed = safeJsonParse(value);
    if (!Array.isArray(parsed))
        return [];
    return parsed.reduce((result, option) => {
        const normalizedOption = normalizeDictOption(option);
        if (normalizedOption)
            result.push(normalizedOption);
        return result;
    }, []);
}
function parseFilterRawValues(value) {
    if (value === null || value === undefined)
        return [];
    if (Array.isArray(value)) {
        return value.flatMap(parseFilterRawValues);
    }
    if (typeof value === 'object') {
        const record = value;
        return [toText(record.key) || toText(record.label)].filter(Boolean);
    }
    const text = toText(value).trim();
    if (!text)
        return [];
    const parsed = safeJsonParse(text);
    if (parsed !== null)
        return parseFilterRawValues(parsed);
    return text.split(',').map((item) => item.trim()).filter(Boolean);
}
function resolveFilterValues(value, options) {
    const keys = [];
    const labels = [];
    parseFilterRawValues(value).forEach((rawValue) => {
        const matchedOption = options.find((option) => option.key === rawValue || option.label === rawValue);
        keys.push(matchedOption?.key || rawValue);
        labels.push(matchedOption?.label || rawValue);
    });
    return {
        keys: unique(keys),
        labels: unique(labels),
    };
}
function resolveTenant(value, kind) {
    const tenantKey = toText(value);
    const tenantOptions = kind === 'skill' ? skillTenantFilterOptions : pluginTenantFilterOptions;
    const matchedTenant = tenantOptions.find((option) => option.key === tenantKey);
    const fallbackLabel = kind === 'skill' ? '租户技能' : '租户插件';
    if (!tenantKey) {
        return {
            key: undefined,
            label: fallbackLabel,
        };
    }
    return {
        key: matchedTenant?.key || tenantKey,
        label: matchedTenant?.label || fallbackLabel,
    };
}
function matchFilter(value, keys, labels) {
    if (!value)
        return true;
    return keys.includes(value) || labels.includes(value);
}
function mapPackageToSkill(item, filters) {
    const id = toText(item.id);
    const title = toText(item.title, toText(item.skillName, toText(item.name, id)));
    const name = toText(item.name, title);
    const description = resolveSkillDescription(item.skillDescription, item.info);
    const updatedAt = toText(item.latestTime);
    const author = toText(item.creatorName);
    const version = toText(item.latestVersion);
    const readmeFile = toText(item.readmeFile);
    const changeLog = toText(item.changeLog);
    const productLines = resolveFilterValues(item.skillProduct, filters.products);
    const categories = resolveFilterValues(item.skillCategory, filters.categories);
    const sources = resolveFilterValues(item.skillSource, filters.sources);
    const tenant = resolveTenant(item.ytenantid, 'skill');
    return {
        id,
        name,
        title,
        description,
        productLineKeys: productLines.keys,
        productLines: productLines.labels,
        categoryKeys: categories.keys,
        categories: categories.labels,
        sourceKey: sources.keys[0],
        source: sources.labels[0],
        tenantKey: tenant.key || undefined,
        tenant: tenant.label || undefined,
        views: toNumber(item.visitVolume),
        rating: toRating(item.rating),
        downloads: toNumber(item.download),
        activations: toNumber(item.skill_active_count),
        author,
        updatedAt,
        documentUrl: readmeFile || undefined,
        changeLog: changeLog || undefined,
        color: pickColor(title),
        icon: 'database',
        iconUrl: resolvePackageIconUrl(item),
        versions: buildVersion(version, readmeFile, updatedAt, changeLog),
    };
}
function mapPackageHistoryItem(item) {
    const version = toText(item.version);
    if (!version)
        return null;
    const id = toText(item.id, version);
    return {
        id,
        name: toText(item.name),
        version,
        createdAt: toText(item.creatTime),
        changeLog: toText(item.changeLog) || undefined,
        creatorName: toText(item.creatorName) || undefined,
        packageUrl: toText(item.CDN) || undefined,
    };
}
function mapYnfEntries(entries) {
    if (!Array.isArray(entries))
        return [];
    return entries.reduce((result, entry) => {
        if (!entry || typeof entry !== 'object')
            return result;
        const code = toText(entry.path);
        const name = toText(entry.name) || code;
        if (!name && !code)
            return result;
        result.push({
            code,
            name,
            description: toText(entry.description),
        });
        return result;
    }, []);
}
function mapPackageToPlugin(item, filters) {
    const ynf = safeJsonParse(item.ynfVersionInfo);
    const id = toText(item.id);
    const packageName = toText(item.name, id);
    const name = ynf?.name || toText(item.skillName, packageName);
    const description = ynf?.description || toText(item.skillDescription, toText(item.info));
    const author = formatAuthor(ynf?.author, toText(item.creatorName));
    const updatedAt = toText(item.latestTime);
    const version = ynf?.version || toText(item.latestVersion);
    const readmeFile = toText(item.readmeFile);
    const changeLog = toText(item.changeLog);
    const workflows = mapYnfEntries(ynf?.workflows);
    const agents = mapYnfEntries(ynf?.agents);
    const skills = mapYnfEntries(ynf?.skills);
    const tools = mapYnfEntries(ynf?.tools);
    const pluginTypes = resolveFilterValues(item.pluginType, filters.types);
    const resolvedPluginTypeKeys = pluginTypes.keys.length ? pluginTypes.keys : [PLUGIN_FALLBACK_TYPE_KEY];
    const resolvedPluginTypeLabels = pluginTypes.labels.length ? pluginTypes.labels : [PLUGIN_FALLBACK_TYPE_LABEL];
    const pluginSources = resolveFilterValues(item.pluginSource, filters.sources);
    const tenant = resolveTenant(item.ytenantid, 'plugin');
    return {
        id,
        packageName,
        name,
        description,
        version,
        typeKeys: resolvedPluginTypeKeys,
        typeLabels: resolvedPluginTypeLabels,
        sourceKeys: pluginSources.keys,
        sourceLabels: pluginSources.labels,
        tenantKey: tenant.key || undefined,
        tenant: tenant.label || undefined,
        views: toNumber(item.visitVolume),
        rating: toRating(item.rating),
        usageCount: toNumber(item.download),
        author,
        updatedAt,
        color: pickColor(name),
        iconUrl: resolvePackageIconUrl(item),
        doc: description,
        documentUrl: readmeFile || undefined,
        changeLog: changeLog || undefined,
        workflows,
        agents,
        skills,
        tools,
        versions: buildVersion(version, readmeFile, updatedAt, changeLog),
    };
}
function isOfficialSkill(skill) {
    return skill.tenantKey === '0';
}
async function fetchPackageList(params) {
    const url = new URL(PACKAGE_LIST_API);
    url.searchParams.set('pageNumber', '1');
    url.searchParams.set('pageSize', String(MARKET_FETCH_PAGE_SIZE));
    url.searchParams.set('hotOrNew', '1');
    Object.entries(params).forEach(([key, value]) => {
        url.searchParams.set(key, String(value));
    });
    const response = await fetch(url.toString());
    if (!response.ok) {
        throw new Error(`Package API request failed: ${response.status}`);
    }
    const json = await response.json();
    if (json.status !== 200 || !json.data) {
        throw new Error(json.message || 'Package API returned invalid data');
    }
    return Array.isArray(json.data.list) ? json.data.list : [];
}
async function fetchPackageHistory(packageName) {
    const url = new URL(PACKAGE_HISTORY_API);
    url.searchParams.set('packagename', packageName);
    url.searchParams.set('pageNumber', '1');
    url.searchParams.set('version', '');
    url.searchParams.set('pageSize', String(PACKAGE_HISTORY_PAGE_SIZE));
    url.searchParams.set('auth', '1');
    const response = await fetch(url.toString());
    if (!response.ok) {
        throw new Error(`Package history API request failed: ${response.status}`);
    }
    const json = await response.json();
    if (json.status !== 200 || !json.data) {
        throw new Error(json.message || 'Package history API returned invalid data');
    }
    const list = Array.isArray(json.data.list) ? json.data.list : [];
    return list.reduce((result, item) => {
        const historyItem = mapPackageHistoryItem(item);
        if (historyItem)
            result.push(historyItem);
        return result;
    }, []);
}
async function fetchSkillFilterOptions() {
    const url = new URL(DICT_LIST_API);
    url.searchParams.set('dict_type', SKILL_FILTER_DICT_TYPE);
    const response = await fetch(url.toString());
    if (!response.ok) {
        throw new Error(`Skill filter API request failed: ${response.status}`);
    }
    const json = await response.json();
    if (json.status !== 200 || json.success === false || !Array.isArray(json.data)) {
        throw new Error(json.message || 'Skill filter API returned invalid data');
    }
    return json.data.reduce((result, item) => {
        if (item.dict_key === 'skill_products') {
            return { ...result, products: parseDictOptions(item.dict_value) };
        }
        if (item.dict_key === 'skill_categories') {
            return { ...result, categories: parseDictOptions(item.dict_value) };
        }
        if (item.dict_key === 'skill_sources') {
            return { ...result, sources: parseDictOptions(item.dict_value) };
        }
        return result;
    }, emptySkillFilters);
}
async function fetchPluginFilterOptions() {
    const url = new URL(DICT_LIST_API);
    url.searchParams.set('dict_type', PLUGIN_FILTER_DICT_TYPE);
    const response = await fetch(url.toString());
    if (!response.ok) {
        throw new Error(`Plugin filter API request failed: ${response.status}`);
    }
    const json = await response.json();
    if (json.status !== 200 || json.success === false || !Array.isArray(json.data)) {
        throw new Error(json.message || 'Plugin filter API returned invalid data');
    }
    return json.data.reduce((result, item) => {
        if (item.dict_key === 'plugin_types') {
            return { ...result, types: parseDictOptions(item.dict_value) };
        }
        if (item.dict_key === 'plugin_sources') {
            return { ...result, sources: parseDictOptions(item.dict_value) };
        }
        return result;
    }, emptyPluginFilters);
}
async function getRemoteSkillFilters() {
    if (!skillFiltersPromise) {
        skillFiltersPromise = fetchSkillFilterOptions().catch((error) => {
            console.error(error);
            return emptySkillFilters;
        });
    }
    return skillFiltersPromise;
}
async function getRemotePluginFilters() {
    if (!pluginFiltersPromise) {
        pluginFiltersPromise = fetchPluginFilterOptions().catch((error) => {
            console.error(error);
            return emptyPluginFilters;
        });
    }
    return pluginFiltersPromise;
}
async function getRemoteSkills() {
    if (!skillItemsPromise) {
        skillItemsPromise = Promise.all([
            fetchPackageList({ isSkillPublish: 1 }),
            getRemoteSkillFilters(),
        ])
            .then(([items, filters]) => items.map((item) => mapPackageToSkill(item, filters)))
            .catch((error) => {
            console.error(error);
            return [];
        });
    }
    return skillItemsPromise;
}
async function getRemotePlugins() {
    if (!pluginItemsPromise) {
        pluginItemsPromise = Promise.all([
            fetchPackageList({ type: 'plugin' }),
            getRemotePluginFilters(),
        ])
            .then(([items, filters]) => items.map((item) => mapPackageToPlugin(item, filters)))
            .catch((error) => {
            console.error(error);
            return [];
        });
    }
    return pluginItemsPromise;
}
export async function getSkills(params = {}) {
    const skills = await getRemoteSkills();
    const officialSkills = skills.filter(isOfficialSkill);
    const keyword = normalize(params.keyword);
    const filtered = officialSkills.filter((skill) => {
        const matchesCategory = params.category === SKILL_FALLBACK_CATEGORY_KEY
            ? matchFilter(params.category, skill.categoryKeys, skill.categories) || (!skill.categoryKeys.length && !skill.categories.length)
            : matchFilter(params.category, skill.categoryKeys, skill.categories);
        return (includesKeyword([
            skill.title,
            skill.name,
            skill.description,
            skill.source,
            skill.tenant,
            ...skill.categories,
            ...skill.productLines,
        ], keyword) &&
            matchesCategory);
    });
    return paginate(filtered, params.page, params.pageSize);
}
export async function getSkillFilters() {
    return getRemoteSkillFilters();
}
export async function getPluginFilters() {
    return getRemotePluginFilters();
}
export async function getSkillDetail(id) {
    const skills = await getRemoteSkills();
    return skills.filter(isOfficialSkill).find((skill) => skill.id === id || skill.name === id) || null;
}
async function getPackageVersionHistory(packageName) {
    if (!packageName)
        return [];
    if (!packageHistoryPromises.has(packageName)) {
        packageHistoryPromises.set(packageName, fetchPackageHistory(packageName).catch((error) => {
            console.error(error);
            return [];
        }));
    }
    return packageHistoryPromises.get(packageName) || [];
}
export async function getSkillVersionHistory(packageName) {
    return getPackageVersionHistory(packageName);
}
export async function getPluginVersionHistory(packageName) {
    return getPackageVersionHistory(packageName);
}
export async function getPlugins(params = {}) {
    const plugins = await getRemotePlugins();
    const keyword = normalize(params.keyword);
    const filtered = plugins.filter((plugin) => {
        return (includesKeyword([
            plugin.name,
            plugin.packageName,
            plugin.description,
            plugin.tenant,
            ...plugin.typeLabels,
            ...plugin.sourceLabels,
        ], keyword) &&
            matchFilter(params.pluginType, plugin.typeKeys, plugin.typeLabels));
    });
    const sorted = [...filtered].sort((a, b) => {
        return toTimestamp(b.updatedAt) - toTimestamp(a.updatedAt);
    });
    return paginate(sorted, params.page, params.pageSize || 6);
}
export async function getPluginDetail(id) {
    const plugins = await getRemotePlugins();
    return plugins.find((plugin) => plugin.id === id || plugin.name === id || plugin.packageName === id) || null;
}
